/**
 * Market Metrics Helper
 *
 * Provides utilities for fetching and formatting market-level statistics
 * from Athena's sellerDashboardMetrics service.
 */
import type { GetMarketMetricsQueryQuery } from '../__generated__/athena';
import { getAthenaClient } from '../components/api';

/**
 * Market metrics response containing current week's statistics
 */
export type MarketMetrics =
  GetMarketMetricsQueryQuery['sellerDashboardMetrics']['getMarketMetrics'];

/**
 * Fetches current market-level metrics for a given market identifier
 *
 * @param marketIdentifier - The market identifier (e.g., "atlanta", "phoenix", "dallas")
 * @returns Market metrics including homes sold, new listings, delistings, and week-over-week changes
 */
export async function getMarketMetrics(
  marketIdentifier: string,
): Promise<MarketMetrics | null | undefined> {
  const result = await getAthenaClient().GetMarketMetricsQuery({
    marketIdentifier,
  });

  return result?.sellerDashboardMetrics?.getMarketMetrics;
}

/**
 * Format week-over-week change as a percentage string with arrow indicator
 * @param wow - Week-over-week change as a decimal (e.g., 0.15 = 15% increase)
 */
export function formatWowChange(wow: number | undefined | null): string {
  if (wow === undefined || wow === null) {
    return 'N/A';
  }

  const arrow = wow >= 0 ? '↗' : '↘';
  const percentage = Math.round(Math.abs(wow) * 100);

  return `${arrow} ${percentage}%`;
}

/**
 * Format market hotness score (0-1) as a percentage
 * @param hotness - Market hotness score between 0 and 1
 */
export function formatMarketHotness(hotness: number | undefined | null): string {
  if (hotness === undefined || hotness === null) {
    return 'N/A';
  }

  return `${Math.round(hotness * 100)}%`;
}
